// pages/BroadbandSearch.jsx  (or components/BroadbandSearch/index.jsx)
// Completely standalone — not tied to any other CRM feature.
// Add to your router:  <Route path="/broadband-search" element={<BroadbandSearch />} />

import React, { useState, useEffect } from "react";
import {
  Wifi,
  Search,
  Plus,
  RefreshCw,
  Loader,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  X,
  Filter,
  Zap,
  MapPin,
  Package,
  ClipboardList,
  ThumbsUp,
  ArrowRight,
  ChevronLeft,
  Building2,
  User,
  Info,
  ExternalLink,
  Tag,
  Trash2,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

// ─── Constants ────────────────────────────────────────────────────────────────

const PROVIDERS = [
  { key: "ITS", label: "ITS Technology", orderable: true },
  { key: "ECLIPSE", label: "Eclipse", orderable: false },
];

const STATUS_META = {
  searched: { label: "Searched", color: "text-gray-400 bg-gray-400/10" },
  quote_saved: {
    label: "Quote Saved",
    color: "text-yellow-400 bg-yellow-400/10",
  },
  quote_accepted: { label: "Accepted", color: "text-blue-400 bg-blue-400/10" },
  data_submitted: {
    label: "Data Submitted",
    color: "text-purple-400 bg-purple-400/10",
  },
  ordered: { label: "Ordered", color: "text-green-400 bg-green-400/10" },
  cancelled: { label: "Cancelled", color: "text-red-400 bg-red-400/10" },
};

const ITS_STEPS = [
  { id: "search", label: "Search", icon: Search },
  { id: "results", label: "Results", icon: Wifi },
  { id: "details", label: "Site Details", icon: ClipboardList },
  { id: "confirm", label: "Confirm", icon: ThumbsUp },
];
const ECLIPSE_STEPS = [
  { id: "address", label: "Address", icon: MapPin },
  { id: "results", label: "Products", icon: Wifi },
  { id: "record", label: "Record", icon: ClipboardList },
];

// ─── Shared small components ──────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.searched;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${m.color}`}
    >
      {m.label}
    </span>
  );
};

const StepBar = ({ steps, currentStep, isDarkMode }) => {
  const ci = steps.findIndex((s) => s.id === currentStep);
  return (
    <div className="flex items-center gap-0 mb-7">
      {steps.map((step, idx) => {
        const done = idx < ci;
        const active = idx === ci;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.id}>
            <div className="flex flex-col items-center">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${done ? "bg-blue-600 border-blue-600" : active ? "border-blue-500 bg-blue-500/10" : isDarkMode ? "border-gray-700" : "border-gray-300"}`}
              >
                {done ? (
                  <CheckCircle size={16} className="text-white" />
                ) : (
                  <Icon
                    size={15}
                    className={
                      active
                        ? "text-blue-400"
                        : isDarkMode
                          ? "text-gray-600"
                          : "text-gray-400"
                    }
                  />
                )}
              </div>
              <span
                className={`text-xs mt-1 font-medium ${active ? "text-blue-400" : done ? (isDarkMode ? "text-gray-300" : "text-gray-600") : isDarkMode ? "text-gray-600" : "text-gray-400"}`}
              >
                {step.label}
              </span>
            </div>
            {idx < steps.length - 1 && (
              <div
                className={`flex-1 h-0.5 mx-1 mb-5 ${idx < ci ? "bg-blue-600" : isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const QuoteCard = ({ quote, selected, onSelect, isDarkMode }) => (
  <button
    onClick={() => onSelect(quote)}
    className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selected ? "border-blue-500 bg-blue-500/10" : isDarkMode ? "border-gray-700 bg-gray-800/50 hover:border-gray-500" : "border-gray-200 bg-white hover:border-gray-300"}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-blue-400 bg-blue-400/10">
            {(quote.supplier || "").toUpperCase()}
          </span>
          {quote.lineType && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-400/10 text-gray-400">
              {quote.lineType.toUpperCase()}
            </span>
          )}
          {!quote.isOffnet && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-400/10 text-green-400">
              On-Net
            </span>
          )}
        </div>
        <p
          className={`font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
        >
          {quote.productName}
        </p>
        {(quote.downlinkSpeed || quote.uplinkSpeed) && (
          <p
            className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            ↓ {quote.downlinkSpeed} Mbps / ↑ {quote.uplinkSpeed} Mbps
          </p>
        )}
        {quote.termMonths && (
          <p
            className={`text-xs mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {quote.termMonths}-month term
          </p>
        )}
        {quote.careOptions?.length > 0 && (
          <div className="flex gap-1 mt-2 flex-wrap">
            {quote.careOptions.map((c) => (
              <span
                key={c}
                className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"}`}
              >
                {c}
              </span>
            ))}
          </div>
        )}
        {quote.additionalInformation?.length > 0 && (
          <ul
            className={`mt-2 space-y-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {quote.additionalInformation.slice(0, 3).map((info, i) => (
              <li key={i} className="flex items-center gap-1 text-xs">
                <CheckCircle size={10} className="text-green-400 shrink-0" />
                {info.title}
                {info.content ? `: ${info.content}` : ""}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="text-right shrink-0">
        {quote.monthlyCost != null ? (
          <p className="text-xl font-bold text-blue-400">
            £{Number(quote.monthlyCost).toFixed(2)}
            <span
              className={`text-xs font-normal ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              /mo
            </span>
          </p>
        ) : (
          <p
            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            Price TBC
          </p>
        )}
        {quote.installCost > 0 && (
          <p
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            + £{Number(quote.installCost).toFixed(2)} install
          </p>
        )}
      </div>
    </div>
    {selected && (
      <div className="mt-2 flex items-center gap-1 text-blue-400 text-xs font-semibold">
        <CheckCircle size={12} /> Selected
      </div>
    )}
  </button>
);

// ─── Search Modal ─────────────────────────────────────────────────────────────

const SearchModal = ({ onClose, onComplete }) => {
  const { isDarkMode } = useTheme();
  const [provider, setProvider] = useState("ITS");
  const [step, setStep] = useState("search");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ITS address
  const [itsAddr, setItsAddr] = useState({
    postcode: "",
    uprn: "",
    address_line_1: "",
    town: "",
    county: "",
    premise: "",
    thoroughfare: "",
  });
  const setA = (k, v) => setItsAddr((p) => ({ ...p, [k]: v }));

  // Eclipse
  const [eclipsePostcode, setEclipsePostcode] = useState("");
  const [eclipseAddresses, setEclipseAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Search label / optional account link
  const [label, setLabel] = useState("");

  // Results
  const [searchId, setSearchId] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [supportsOrdering, setSupportsOrdering] = useState(true);

  // ITS quote
  const [customerName, setCustomerName] = useState("");
  const [customRef, setCustomRef] = useState("");
  const [layer, setLayer] = useState(3);
  const [quoteConfig, setQuoteConfig] = useState(null);
  const [providerQuoteRef, setProviderQuoteRef] = useState(null);

  // ITS site details
  const [site, setSite] = useState({
    site_contact_name: "",
    site_contact_telephone_number: "",
    secondary_site_contact_name: "",
    secondary_site_contact_telephone_number: "",
    interface_type: "Copper",
    owned_or_leased: true,
    landlord_wayleave_required: false,
    landlord_name: "",
    landlord_contact_number: "",
    landlord_email_address: "",
    has_access_restrictions: false,
    access_restrictions_details: "",
    is_site_ready: true,
    site_ready_date: "",
    built_after_2000: false,
    asbestos_register_on_site: "Unknown",
    require_ssrams: false,
    floor_reference: "",
    room_reference: "",
    mains_within_3m: true,
    rack_space_available: "Wall",
    rack_mounted_required: false,
  });
  const setS = (k, v) => setSite((p) => ({ ...p, [k]: v }));

  // Eclipse record
  const [eclipseOrderRef, setEclipseOrderRef] = useState("");
  const [eclipseMonthlyCost, setEclipseMonthlyCost] = useState("");
  const [eclipseNotes, setEclipseNotes] = useState("");

  // Done
  const [providerOrderRef, setProviderOrderRef] = useState(null);

  const isEclipse = provider === "ECLIPSE";
  const steps = isEclipse ? ECLIPSE_STEPS : ITS_STEPS;

  useEffect(() => {
    setStep(isEclipse ? "address" : "search");
    setQuotes([]);
    setSelectedQuote(null);
    setSearchId(null);
    setError(null);
    setEclipseAddresses([]);
    setSelectedAddress(null);
  }, [provider]);

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white placeholder-gray-500" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`;
  const labelCls = `block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`;
  const cardCls = `${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"} rounded-xl border p-5`;
  const btnPrimary =
    "flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
  const btnSecondary = `flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-200" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleEclipseAddressSearch = async () => {
    if (!eclipsePostcode.trim()) {
      setError("Postcode is required");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await apiService.eclipseAddressLookup(eclipsePostcode.trim());
      setEclipseAddresses(res.data || []);
      if (!(res.data || []).length) setError("No addresses found");
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEclipseAvailability = async () => {
    if (!selectedAddress) {
      setError("Select an address");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await apiService.runBroadbandSearch({
        provider: "ECLIPSE",
        addressReference: selectedAddress.addressReference,
        label: label || undefined,
      });
      setSearchId(res.data.id);
      setQuotes(res.data.quotes || []);
      setSupportsOrdering(res.data.supportsOrdering);
      setStep("results");
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleItsSearch = async () => {
    if (!itsAddr.postcode.trim()) {
      setError("Postcode is required");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload = {
        provider: "ITS",
        postcode: itsAddr.postcode.trim(),
        label: label || undefined,
      };
      [
        "uprn",
        "address_line_1",
        "town",
        "county",
        "premise",
        "thoroughfare",
      ].forEach((k) => {
        if (itsAddr[k]?.trim()) payload[k] = itsAddr[k].trim();
      });
      const res = await apiService.runBroadbandSearch(payload);
      setSearchId(res.data.id);
      setQuotes(res.data.quotes || []);
      setSupportsOrdering(res.data.supportsOrdering);
      setStep("results");
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleItsSaveQuote = async () => {
    if (customerName.trim().length < 5) {
      setError("Customer name must be at least 5 characters");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await apiService.saveBroadbandQuote(searchId, {
        quoteId: selectedQuote.providerQuoteId,
        customer_name: customerName.trim(),
        custom_reference: customRef.trim() || undefined,
        layer,
      });
      setProviderQuoteRef(res.data.providerQuoteRef);
      await apiService.acceptBroadbandQuote(searchId);
      try {
        const cfg = await apiService.getBroadbandQuoteConfig(searchId);
        setQuoteConfig(cfg.data || cfg);
        const ifTypes = Object.keys(cfg.data?.interface_types || {});
        if (ifTypes.length) setS("interface_type", ifTypes[0]);
      } catch {
        /* optional */
      }
      setStep("details");
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleItsSubmitDetails = async () => {
    if (!site.site_contact_name.trim()) {
      setError("Site contact name required");
      return;
    }
    if (!site.site_contact_telephone_number.trim()) {
      setError("Site contact phone required");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const payload = {
        ...site,
        selected_layer: layer,
        owned_or_leased: Boolean(site.owned_or_leased),
        has_access_restrictions: Boolean(site.has_access_restrictions),
        is_site_ready: Boolean(site.is_site_ready),
        built_after_2000: Boolean(site.built_after_2000),
        require_ssrams: Boolean(site.require_ssrams),
        mains_within_3m: Boolean(site.mains_within_3m),
        rack_mounted_required: Boolean(site.rack_mounted_required),
      };
      [
        "floor_reference",
        "room_reference",
        "access_restrictions_details",
        "site_ready_date",
        "landlord_name",
        "landlord_contact_number",
        "landlord_email_address",
        "secondary_site_contact_name",
        "secondary_site_contact_telephone_number",
      ].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await apiService.submitBroadbandData(searchId, payload);
      setStep("confirm");
    } catch (e) {
      const msg = e.response?.data?.errors
        ? Object.values(e.response.data.errors).flat().join(", ")
        : e.response?.data?.message || e.message || "Failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleItsApprove = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await apiService.approveBroadbandOrder(searchId);
      setProviderOrderRef(res.data.providerOrderRef);
      setStep("done");
      onComplete?.();
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEclipseMarkOrdered = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await apiService.markBroadbandOrdered(searchId, {
        selectedProductCode: selectedQuote?.productCode,
        providerOrderRef: eclipseOrderRef.trim() || undefined,
        monthlyCost: eclipseMonthlyCost
          ? parseFloat(eclipseMonthlyCost)
          : undefined,
        notes: eclipseNotes.trim() || undefined,
      });
      setProviderOrderRef(res.data.providerOrderRef || eclipseOrderRef || null);
      setStep("done");
      onComplete?.();
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const prevStep = () => {
    const map = isEclipse
      ? { results: "address", record: "results" }
      : { results: "search", details: "results", confirm: "details" };
    setStep(map[step] || (isEclipse ? "address" : "search"));
    setError(null);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className={`relative w-full max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border shadow-2xl flex flex-col ${isDarkMode ? "bg-[#0E1117] border-gray-800" : "bg-gray-50 border-gray-200"}`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
        >
          <div>
            <h2
              className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              New Broadband Search
            </h2>
            <p
              className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Check availability and place orders across providers
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Provider selector — first step only */}
          {(step === "search" || step === "address") && (
            <div className="flex gap-2 mb-6 flex-wrap">
              {PROVIDERS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setProvider(p.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${provider === p.key ? "border-blue-500 bg-blue-500/15 text-blue-400" : isDarkMode ? "border-gray-700 text-gray-400 hover:border-gray-500" : "border-gray-200 text-gray-600 hover:border-gray-300"}`}
                >
                  {p.label}
                  {!p.orderable && (
                    <span
                      className={`ml-2 text-xs px-1.5 py-0.5 rounded ${isDarkMode ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400"}`}
                    >
                      Availability only
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {step !== "done" && (
            <StepBar steps={steps} currentStep={step} isDarkMode={isDarkMode} />
          )}

          {error && (
            <div className="flex items-start gap-2 mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ── ITS: search ──────────────────────────────────────────────── */}
          {!isEclipse && step === "search" && (
            <div className="space-y-4">
              <div className={cardCls}>
                <div className="mb-3">
                  <label className={labelCls}>Search Label (optional)</label>
                  <input
                    className={inputCls}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Head office, Site B..."
                  />
                </div>
                <h3
                  className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <MapPin size={14} className="text-blue-400" /> Address
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>
                      Postcode <span className="text-red-400">*</span>
                    </label>
                    <input
                      className={inputCls}
                      value={itsAddr.postcode}
                      onChange={(e) => setA("postcode", e.target.value)}
                      placeholder="e.g. WD17 2RB"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className={labelCls}>UPRN</label>
                    <input
                      className={inputCls}
                      value={itsAddr.uprn}
                      onChange={(e) => setA("uprn", e.target.value)}
                      placeholder="e.g. 100080936880"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className={labelCls}>Address Line 1</label>
                    <input
                      className={inputCls}
                      value={itsAddr.address_line_1}
                      onChange={(e) => setA("address_line_1", e.target.value)}
                      placeholder="e.g. 20 Gladstone Road"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Town</label>
                    <input
                      className={inputCls}
                      value={itsAddr.town}
                      onChange={(e) => setA("town", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>County</label>
                    <input
                      className={inputCls}
                      value={itsAddr.county}
                      onChange={(e) => setA("county", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Premise No.</label>
                    <input
                      className={inputCls}
                      value={itsAddr.premise}
                      onChange={(e) => setA("premise", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Street Name</label>
                    <input
                      className={inputCls}
                      value={itsAddr.thoroughfare}
                      onChange={(e) => setA("thoroughfare", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Eclipse: address lookup ───────────────────────────────────── */}
          {isEclipse && step === "address" && (
            <div className="space-y-4">
              <div className={cardCls}>
                <div className="mb-4">
                  <label className={labelCls}>Search Label (optional)</label>
                  <input
                    className={inputCls}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Head office..."
                  />
                </div>
                <h3
                  className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <MapPin size={14} className="text-orange-400" /> Postcode
                  Address Search
                </h3>
                <div className="flex gap-2">
                  <input
                    className={`${inputCls} flex-1`}
                    value={eclipsePostcode}
                    onChange={(e) => setEclipsePostcode(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleEclipseAddressSearch()
                    }
                    placeholder="e.g. NE61 1XD"
                  />
                  <button
                    onClick={handleEclipseAddressSearch}
                    disabled={loading}
                    className={`${btnPrimary} bg-orange-600 hover:bg-orange-700`}
                  >
                    {loading ? (
                      <Loader size={14} className="animate-spin" />
                    ) : (
                      <Search size={14} />
                    )}{" "}
                    Find
                  </button>
                </div>
                {eclipseAddresses.length > 0 && (
                  <div className="mt-4">
                    <label className={`${labelCls} mb-2`}>
                      Select Address ({eclipseAddresses.length} found)
                    </label>
                    <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                      {eclipseAddresses.map((addr, i) => {
                        const ref = addr.addressReference;
                        const sel = selectedAddress === addr;
                        return (
                          <button
                            key={i}
                            onClick={() => setSelectedAddress(addr)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${sel ? "border-orange-500 bg-orange-500/10" : isDarkMode ? "border-gray-700 bg-gray-800/50 hover:border-gray-600" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                          >
                            <p
                              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {addr.displayLabel}
                            </p>
                            <p
                              className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {ref.qualifier}
                              {ref.addressReferenceNumber
                                ? ` · ${ref.addressReferenceNumber}`
                                : ""}
                              {ref.uprn ? ` · UPRN ${ref.uprn}` : ""}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Results (shared) ──────────────────────────────────────────── */}
          {step === "results" && (
            <div className="space-y-4">
              {isEclipse && (
                <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                  <Info size={14} className="text-orange-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-orange-300">
                    Eclipse does not expose pricing via API. Select a product,
                    place the order in the <strong>Eclipse portal</strong>, then
                    record the reference.
                  </p>
                </div>
              )}
              <div className="flex items-center justify-between">
                <p
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                >
                  {quotes.length} product{quotes.length !== 1 ? "s" : ""}{" "}
                  available
                </p>
              </div>
              {quotes.length === 0 ? (
                <div
                  className={`text-center py-12 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                >
                  <Wifi size={40} className="mx-auto mb-3 opacity-40" />
                  <p>No products available for this address.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {quotes.map((q) => (
                    <QuoteCard
                      key={q.providerQuoteId || q.productCode}
                      quote={q}
                      selected={
                        isEclipse
                          ? selectedQuote?.productCode === q.productCode
                          : selectedQuote?.providerQuoteId === q.providerQuoteId
                      }
                      onSelect={setSelectedQuote}
                      isDarkMode={isDarkMode}
                    />
                  ))}
                </div>
              )}
              {!isEclipse && selectedQuote && (
                <div className={`${cardCls} mt-4`}>
                  <h3
                    className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Quote Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Customer Name *</label>
                      <input
                        className={inputCls}
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        placeholder="Min 5 characters"
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Custom Reference</label>
                      <input
                        className={inputCls}
                        value={customRef}
                        onChange={(e) => setCustomRef(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className={labelCls}>Layer</label>
                      <select
                        className={inputCls}
                        value={layer}
                        onChange={(e) => setLayer(Number(e.target.value))}
                      >
                        <option value={2}>Layer 2</option>
                        <option value={3}>Layer 3</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── ITS: site details ─────────────────────────────────────────── */}
          {!isEclipse && step === "details" && (
            <div className="space-y-4">
              {providerQuoteRef && (
                <div className="flex items-center gap-2 text-xs text-green-400 bg-green-400/10 border border-green-400/20 rounded-lg px-3 py-2">
                  <CheckCircle size={13} /> Quote saved · Ref:{" "}
                  <strong>{providerQuoteRef}</strong>
                </div>
              )}
              <div className={cardCls}>
                <h3
                  className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <User size={14} className="text-blue-400" />
                  Site Contacts
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {quoteConfig?.contacts?.length > 0 && (
                    <div className="col-span-2">
                      <label className={labelCls}>Primary Contact</label>
                      <select
                        className={inputCls}
                        value={site.primary_contact || ""}
                        onChange={(e) =>
                          setS("primary_contact", e.target.value)
                        }
                      >
                        <option value="">Select...</option>
                        {quoteConfig.contacts.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.text}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div>
                    <label className={labelCls}>Site Contact Name *</label>
                    <input
                      className={inputCls}
                      value={site.site_contact_name}
                      onChange={(e) =>
                        setS("site_contact_name", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Site Contact Phone *</label>
                    <input
                      className={inputCls}
                      value={site.site_contact_telephone_number}
                      onChange={(e) =>
                        setS("site_contact_telephone_number", e.target.value)
                      }
                      placeholder="07700900000"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Secondary Name</label>
                    <input
                      className={inputCls}
                      value={site.secondary_site_contact_name}
                      onChange={(e) =>
                        setS("secondary_site_contact_name", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Secondary Phone</label>
                    <input
                      className={inputCls}
                      value={site.secondary_site_contact_telephone_number}
                      onChange={(e) =>
                        setS(
                          "secondary_site_contact_telephone_number",
                          e.target.value,
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={cardCls}>
                <h3
                  className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Zap size={14} className="text-blue-400" />
                  Technical
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Interface Type</label>
                    <select
                      className={inputCls}
                      value={site.interface_type}
                      onChange={(e) => setS("interface_type", e.target.value)}
                    >
                      {quoteConfig?.interface_types ? (
                        Object.values(quoteConfig.interface_types).map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.text}
                          </option>
                        ))
                      ) : (
                        <option value="Copper">Copper</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Rack Space</label>
                    <select
                      className={inputCls}
                      value={site.rack_space_available}
                      onChange={(e) =>
                        setS("rack_space_available", e.target.value)
                      }
                    >
                      {["1U", "2U", "3U", "4U", "Shelf", "Wall", "None"].map(
                        (v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Floor Ref</label>
                    <input
                      className={inputCls}
                      value={site.floor_reference}
                      onChange={(e) => setS("floor_reference", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Room Ref</label>
                    <input
                      className={inputCls}
                      value={site.room_reference}
                      onChange={(e) => setS("room_reference", e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3">
                  {[
                    { k: "mains_within_3m", l: "Mains within 3m" },
                    { k: "rack_mounted_required", l: "Rack Mounted" },
                    { k: "require_ssrams", l: "SSRAMS" },
                  ].map(({ k, l }) => (
                    <label
                      key={k}
                      className={`flex items-center gap-2 text-sm cursor-pointer ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(site[k])}
                        onChange={(e) => setS(k, e.target.checked)}
                        className="rounded"
                      />{" "}
                      {l}
                    </label>
                  ))}
                </div>
              </div>
              <div className={cardCls}>
                <h3
                  className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Building2 size={14} className="text-blue-400" />
                  Building &amp; Access
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { k: "owned_or_leased", l: "Building Owned" },
                    { k: "built_after_2000", l: "Built After 2000" },
                    { k: "has_access_restrictions", l: "Access Restrictions" },
                    { k: "is_site_ready", l: "Site Ready Now" },
                  ].map(({ k, l }) => (
                    <label
                      key={k}
                      className={`flex items-center gap-2 text-sm cursor-pointer ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(site[k])}
                        onChange={(e) => setS(k, e.target.checked)}
                        className="rounded"
                      />{" "}
                      {l}
                    </label>
                  ))}
                  {!site.built_after_2000 && (
                    <div className="col-span-2">
                      <label className={labelCls}>Asbestos Register</label>
                      <select
                        className={inputCls}
                        value={site.asbestos_register_on_site}
                        onChange={(e) =>
                          setS("asbestos_register_on_site", e.target.value)
                        }
                      >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Unknown">Unknown</option>
                      </select>
                    </div>
                  )}
                  {!site.owned_or_leased && (
                    <>
                      <div>
                        <label className={labelCls}>Landlord Name</label>
                        <input
                          className={inputCls}
                          value={site.landlord_name}
                          onChange={(e) =>
                            setS("landlord_name", e.target.value)
                          }
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Landlord Phone</label>
                        <input
                          className={inputCls}
                          value={site.landlord_contact_number}
                          onChange={(e) =>
                            setS("landlord_contact_number", e.target.value)
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── ITS: confirm ──────────────────────────────────────────────── */}
          {!isEclipse && step === "confirm" && selectedQuote && (
            <div className="space-y-4">
              <div className={`${cardCls} border-blue-500/30`}>
                <h3
                  className={`text-sm font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Order Summary
                </h3>
                <div className="space-y-2">
                  {[
                    ["Provider", "ITS"],
                    ["Product", selectedQuote.productName],
                    [
                      "Monthly",
                      `£${Number(selectedQuote.monthlyCost || 0).toFixed(2)}/mo`,
                    ],
                    [
                      "Install",
                      `£${Number(selectedQuote.installCost || 0).toFixed(2)}`,
                    ],
                    ["Term", `${selectedQuote.termMonths} months`],
                    ["Customer", customerName],
                    ["Quote Ref", providerQuoteRef || "—"],
                    ["Layer", `Layer ${layer}`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        {k}
                      </span>
                      <span
                        className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <Info size={14} className="text-yellow-400 mt-0.5 shrink-0" />
                <p className="text-xs text-yellow-300">
                  By clicking "Place Order" you approve this quote and create a
                  live order with ITS. This cannot be undone.
                </p>
              </div>
            </div>
          )}

          {/* ── Eclipse: record ───────────────────────────────────────────── */}
          {isEclipse && step === "record" && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <ExternalLink
                  size={14}
                  className="text-orange-400 mt-0.5 shrink-0"
                />
                <p className="text-xs text-orange-300">
                  Place this order in the{" "}
                  <strong>Eclipse Wholesale portal</strong>, then record the
                  details here.
                </p>
              </div>
              {selectedQuote && (
                <div className={`${cardCls} border-orange-500/20`}>
                  <p
                    className={`text-xs font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Selected Product
                  </p>
                  <p
                    className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {selectedQuote.productName}
                  </p>
                  {selectedQuote.downlinkSpeed && (
                    <p
                      className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      ↓ {selectedQuote.downlinkSpeed} Mbps / ↑{" "}
                      {selectedQuote.uplinkSpeed} Mbps
                    </p>
                  )}
                </div>
              )}
              <div className={cardCls}>
                <h3
                  className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Tag size={14} className="text-orange-400" />
                  Order Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className={labelCls}>
                      Eclipse Order Reference (optional)
                    </label>
                    <input
                      className={inputCls}
                      value={eclipseOrderRef}
                      onChange={(e) => setEclipseOrderRef(e.target.value)}
                      placeholder="e.g. ECL-12345678"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Monthly Cost (£)</label>
                    <input
                      className={inputCls}
                      type="number"
                      step="0.01"
                      value={eclipseMonthlyCost}
                      onChange={(e) => setEclipseMonthlyCost(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Notes</label>
                    <textarea
                      className={`${inputCls} h-20 resize-none`}
                      value={eclipseNotes}
                      onChange={(e) => setEclipseNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Done ─────────────────────────────────────────────────────── */}
          {step === "done" && (
            <div className="text-center py-10">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isEclipse ? "bg-orange-500/15 border border-orange-500/30" : "bg-green-500/15 border border-green-500/30"}`}
              >
                <CheckCircle
                  size={32}
                  className={isEclipse ? "text-orange-400" : "text-green-400"}
                />
              </div>
              <h3
                className={`text-xl font-bold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {isEclipse ? "Order Recorded!" : "Order Placed!"}
              </h3>
              <p
                className={`text-sm mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {isEclipse
                  ? "The Eclipse order has been recorded."
                  : "Your order has been submitted to ITS."}
              </p>
              {providerOrderRef && (
                <div
                  className={`inline-block px-5 py-3 rounded-xl border ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`}
                >
                  <p
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Order Reference
                  </p>
                  <p className="text-xl font-mono font-bold text-blue-400 mt-1">
                    {providerOrderRef}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step !== "done" ? (
          <div
            className={`flex items-center justify-between px-6 py-4 border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
          >
            <div>
              {step !== "search" && step !== "address" && (
                <button
                  onClick={prevStep}
                  className={btnSecondary}
                  disabled={loading}
                >
                  <ChevronLeft size={15} />
                  Back
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className={btnSecondary}
                disabled={loading}
              >
                Cancel
              </button>

              {!isEclipse && step === "search" && (
                <button
                  onClick={handleItsSearch}
                  className={btnPrimary}
                  disabled={loading || !itsAddr.postcode.trim()}
                >
                  {loading ? (
                    <Loader size={14} className="animate-spin" />
                  ) : (
                    <Search size={14} />
                  )}{" "}
                  Search
                </button>
              )}
              {!isEclipse && step === "results" && (
                <button
                  onClick={() => {
                    if (!selectedQuote) {
                      setError("Select a quote");
                      return;
                    }
                    handleItsSaveQuote();
                  }}
                  className={btnPrimary}
                  disabled={loading || !selectedQuote}
                >
                  {loading ? (
                    <Loader size={14} className="animate-spin" />
                  ) : (
                    <ArrowRight size={14} />
                  )}{" "}
                  Continue
                </button>
              )}
              {!isEclipse && step === "details" && (
                <button
                  onClick={handleItsSubmitDetails}
                  className={btnPrimary}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size={14} className="animate-spin" />
                  ) : (
                    <ArrowRight size={14} />
                  )}{" "}
                  Review Order
                </button>
              )}
              {!isEclipse && step === "confirm" && (
                <button
                  onClick={handleItsApprove}
                  className={`${btnPrimary} bg-green-600 hover:bg-green-700`}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size={14} className="animate-spin" />
                  ) : (
                    <ThumbsUp size={14} />
                  )}{" "}
                  Place Order
                </button>
              )}

              {isEclipse && step === "address" && (
                <button
                  onClick={handleEclipseAvailability}
                  className={`${btnPrimary} bg-orange-600 hover:bg-orange-700`}
                  disabled={loading || !selectedAddress}
                >
                  {loading ? (
                    <Loader size={14} className="animate-spin" />
                  ) : (
                    <Wifi size={14} />
                  )}{" "}
                  Check Availability
                </button>
              )}
              {isEclipse && step === "results" && (
                <button
                  onClick={() => {
                    if (!selectedQuote) {
                      setError("Select a product");
                      return;
                    }
                    setError(null);
                    setStep("record");
                  }}
                  className={`${btnPrimary} bg-orange-600 hover:bg-orange-700`}
                  disabled={!selectedQuote}
                >
                  <ArrowRight size={14} />
                  Continue
                </button>
              )}
              {isEclipse && step === "record" && (
                <button
                  onClick={handleEclipseMarkOrdered}
                  className={`${btnPrimary} bg-orange-600 hover:bg-orange-700`}
                  disabled={loading}
                >
                  {loading ? (
                    <Loader size={14} className="animate-spin" />
                  ) : (
                    <CheckCircle size={14} />
                  )}{" "}
                  Record Order
                </button>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`flex justify-center px-6 py-4 border-t ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
          >
            <button onClick={onClose} className={btnPrimary}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main BroadbandSearch page ────────────────────────────────────────────────

const BroadbandSearch = () => {
  const { isDarkMode } = useTheme();
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterProvider, setFilterProvider] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);

  const fetchSearches = async () => {
    setLoading(true);
    try {
      const res = await apiService.getBroadbandSearches({
        provider: filterProvider || undefined,
        status: filterStatus || undefined,
        search: search || undefined,
      });
      setSearches(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSearches();
  }, [filterProvider, filterStatus, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this search record?")) return;
    try {
      await apiService.deleteBroadbandSearch(id);
      setSearches((p) => p.filter((s) => s.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  const cardCls = `${isDarkMode ? "bg-gray-800/40 border-gray-700" : "bg-white border-gray-200"} rounded-xl border transition-all`;
  const inputCls = `px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`;

  return (
    <div
      className={`min-h-screen p-6 ${isDarkMode ? "bg-[#0E1117] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Broadband Search
          </h1>
          <p
            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Check availability and manage orders across ITS and Eclipse
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
        >
          <Plus size={16} /> New Search
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={15}
            className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          />
          <input
            className={`${inputCls} w-full pl-9`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by label, ref..."
          />
        </div>
        <select
          className={inputCls}
          value={filterProvider}
          onChange={(e) => setFilterProvider(e.target.value)}
        >
          <option value="">All Providers</option>
          {PROVIDERS.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          className={inputCls}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
        <button
          onClick={fetchSearches}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-700 text-gray-400" : "hover:bg-gray-100 text-gray-500"}`}
        >
          {loading ? (
            <Loader size={16} className="animate-spin" />
          ) : (
            <RefreshCw size={16} />
          )}
        </button>
      </div>

      {/* Results */}
      {loading && searches.length === 0 ? (
        <div
          className={`text-center py-20 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          <Loader size={28} className="animate-spin mx-auto mb-3" />
          <p>Loading...</p>
        </div>
      ) : searches.length === 0 ? (
        <div
          className={`text-center py-20 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        >
          <Wifi size={48} className="mx-auto mb-4 opacity-30" />
          <p className="font-medium">No searches yet</p>
          <p className="text-sm mt-1">
            Click "New Search" to check broadband availability.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {searches.map((record) => {
            const isExp = expanded === record.id;
            const selected = record.selectedQuoteRaw;
            return (
              <div key={record.id} className={cardCls}>
                <button
                  onClick={() => setExpanded(isExp ? null : record.id)}
                  className="w-full flex items-center justify-between gap-4 p-4 text-left"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isDarkMode ? "bg-gray-700" : "bg-gray-100"}`}
                    >
                      <Wifi
                        size={18}
                        className={
                          record.provider === "ECLIPSE"
                            ? "text-orange-400"
                            : "text-blue-400"
                        }
                      />
                    </div>
                    <div className="min-w-0">
                      <p
                        className={`font-semibold text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {record.label ||
                          selected?.productName ||
                          `${record.provider} Search`}
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {record.provider} ·{" "}
                        {new Date(record.createdAt).toLocaleDateString()}
                        {record.providerQuoteRef
                          ? ` · ${record.providerQuoteRef}`
                          : ""}
                        {record.providerOrderRef
                          ? ` · ${record.providerOrderRef}`
                          : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {record.monthlyCost && (
                      <span className="text-sm font-bold text-blue-400">
                        £{Number(record.monthlyCost).toFixed(2)}/mo
                      </span>
                    )}
                    <StatusBadge status={record.status} />
                    {isExp ? (
                      <ChevronUp
                        size={15}
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      />
                    ) : (
                      <ChevronDown
                        size={15}
                        className={
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }
                      />
                    )}
                  </div>
                </button>

                {isExp && (
                  <div
                    className={`px-4 pb-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                      {[
                        ["Provider", record.provider],
                        [
                          "Status",
                          STATUS_META[record.status]?.label || record.status,
                        ],
                        record.providerQuoteRef && [
                          "Quote Ref",
                          record.providerQuoteRef,
                        ],
                        record.providerOrderRef && [
                          "Order Ref",
                          record.providerOrderRef,
                        ],
                        record.termMonths && [
                          "Term",
                          `${record.termMonths} months`,
                        ],
                        record.installCost && [
                          "Install",
                          `£${Number(record.installCost).toFixed(2)}`,
                        ],
                        selected?.productName && [
                          "Product",
                          selected.productName,
                        ],
                        selected?.downlinkSpeed && [
                          "Speed",
                          `${selected.downlinkSpeed}↓ / ${selected.uplinkSpeed}↑ Mbps`,
                        ],
                        [
                          record.searchResults?.length
                            ? `${record.searchResults.length} products found`
                            : null,
                          null,
                        ],
                      ]
                        .filter(Boolean)
                        .filter(([k]) => k)
                        .map(([k, v]) => (
                          <div key={k}>
                            <p
                              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              {k}
                            </p>
                            <p
                              className={`text-sm font-medium mt-0.5 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                            >
                              {v || "—"}
                            </p>
                          </div>
                        ))}
                    </div>
                    {record.notes && (
                      <p
                        className={`mt-3 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Notes: {record.notes}
                      </p>
                    )}
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <SearchModal
          onClose={() => setShowModal(false)}
          onComplete={() => {
            setShowModal(false);
            fetchSearches();
          }}
        />
      )}
    </div>
  );
};

export default BroadbandSearch;
